import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, Truck, ShieldCheck, CreditCard, Landmark, Wrench, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useShop } from "@/shop/ShopContext";
import { CATEGORIES, SCOOTER_TAGS, HERO_IMAGE, WORKSHOP_IMAGE } from "@/lib/catalog";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const HERO_LINES = [
  { text: "Ricambi auto,", cls: "text-neutral-100" },
  { text: "moto & scooter", cls: "text-outline" },
  { text: "in pronta consegna", cls: "text-[#FF5500]" },
];

const Home = () => {
  const { config } = useShop();
  const [featured, setFeatured] = useState([]);
  const [copied, setCopied] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => setFeatured(r.data.slice(0, 8)))
      .catch(() => {});
  }, []);

  const copyIban = () => {
    if (!config.iban?.iban) return;
    navigator.clipboard.writeText(config.iban.iban);
    setCopied(true);
    toast.success("IBAN copiato negli appunti");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[92vh] items-center overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Ricambi motore" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0C0E12] via-[#0C0E12]/85 to-[#0C0E12]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0C0E12] via-transparent to-[#0C0E12]/60" />
        </motion.div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FF5500]/40 bg-[#FF5500]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.25em] text-[#FF5500]"
          >
            <Wrench className="h-3.5 w-3.5" />
            Ricambi originali e compatibili
          </motion.div>

          <h1 className="max-w-4xl font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-8xl">
            {HERO_LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  className={`block ${line.cls}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-neutral-300 sm:text-lg"
          >
            Pastiglie, filtri, cinghie, variatori, batterie e ammortizzatori per utilitarie e per
            Honda SH, Sym Twit, Piaggio Beverly e Liberty. Ordina online o al telefono.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/catalogo"
              data-testid="hero-catalog-btn"
              className="group flex items-center gap-3 rounded-full bg-[#FF5500] px-7 py-4 font-display text-base uppercase tracking-[0.2em] text-white transition-all hover:bg-[#E64D00] hover:shadow-[0_10px_40px_rgba(255,85,0,0.4)]"
            >
              Sfoglia il catalogo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href={config.phone_href}
              data-testid="hero-phone-btn"
              className="flex items-center gap-3 rounded-full border border-white/25 px-7 py-4 font-display text-base uppercase tracking-[0.15em] text-neutral-100 backdrop-blur transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
            >
              <Phone className="h-4 w-4" />
              {config.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              { icon: Truck, label: "Spedizione 24/48h" },
              { icon: ShieldCheck, label: "Ricambi garantiti" },
              { icon: CreditCard, label: config.bonifico_enabled ? "Carta o bonifico" : "Pagamento con carta" },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2.5 text-sm text-neutral-300">
                <s.icon className="h-4 w-4 text-[#FF5500]" />
                {s.label}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <Marquee
        items={[
          "Honda SH 125",
          "Honda SH 150",
          "Honda SH 350",
          "Sym Twit 125",
          "Sym Twit 150",
          "Piaggio Beverly 300",
          "Piaggio Liberty 50",
          "Piaggio Liberty 125",
          "Piaggio Liberty 150",
          "Piaggio Liberty 200",
          "Fiat Panda",
          "VW Polo",
          "Toyota Yaris",
        ]}
      />

      {/* CATEGORIE */}
      <section className="mx-auto max-w-7xl px-4 py-24" data-testid="categories-section">
        <Reveal>
          <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">Il magazzino</p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-neutral-100 sm:text-5xl">
            Categorie ricambi
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CATEGORIES.map((cat, i) => (
            <Reveal key={cat.id} delay={i * 0.08} className={i === 0 ? "sm:row-span-2" : ""}>
              <Link
                to={`/catalogo?categoria=${cat.id}`}
                data-testid={`category-card-${cat.id}`}
                className={`group relative block h-full overflow-hidden rounded-xl border border-white/10 transition-colors hover:border-[#FF5500]/60 ${i === 0 ? "min-h-[420px]" : "min-h-[200px]"}`}
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0C0E12] via-[#0C0E12]/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-display text-xl font-semibold uppercase tracking-wide text-neutral-100">
                    {cat.name}
                  </h3>
                  <p className="mt-1 text-xs text-neutral-400">{cat.desc}</p>
                  <span className="mt-3 inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.2em] text-[#FF5500]">
                    Scopri <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SCOOTER TAGS */}
      <section className="border-y border-white/10 bg-[#14171D] py-16" data-testid="scooter-tags-section">
        <div className="mx-auto max-w-7xl px-4">
          <Reveal>
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-neutral-100">
              Cerca per il tuo scooter
            </h2>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            {SCOOTER_TAGS.map((tag, i) => (
              <Reveal key={tag} delay={i * 0.04}>
                <Link
                  to={`/catalogo?veicolo=${encodeURIComponent(tag)}`}
                  data-testid={`scooter-tag-${tag.replace(/\s+/g, "-").toLowerCase()}`}
                  className="rounded-full border border-white/15 bg-[#1C2028] px-5 py-2.5 font-display text-sm uppercase tracking-[0.15em] text-neutral-200 transition-all hover:border-[#FF5500] hover:bg-[#FF5500] hover:text-white"
                >
                  {tag}
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IN EVIDENZA */}
      <section className="mx-auto max-w-7xl px-4 py-24" data-testid="featured-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">Pronta consegna</p>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-neutral-100 sm:text-5xl">
              Ricambi in evidenza
            </h2>
          </div>
          <Link
            to="/catalogo"
            className="flex items-center gap-2 font-display text-sm uppercase tracking-[0.2em] text-[#FF5500] transition-colors hover:text-white"
            data-testid="featured-view-all"
          >
            Vedi tutto <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* WORKSHOP / TRUST */}
      <section className="relative overflow-hidden border-y border-white/10" data-testid="workshop-section">
        <div className="absolute inset-0">
          <img src={WORKSHOP_IMAGE} alt="Officina" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0C0E12]/85" />
        </div>
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-24 lg:grid-cols-2">
          <Reveal>
            <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">Perché noi</p>
            <h2 className="mt-3 font-display text-4xl font-bold uppercase leading-tight tracking-tight text-neutral-100 sm:text-5xl">
              Il ricambio giusto, subito. Senza giri di parole.
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-neutral-300">
              Non trovi il codice o non sei sicuro della compatibilità? Chiamaci: ti diciamo subito se
              il pezzo è disponibile e quanto costa. Spedizione in 24/48h in tutta Italia.
            </p>
            <a
              href={config.phone_href}
              data-testid="workshop-phone-btn"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#FF5500] px-7 py-4 font-display text-base uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
            >
              <Phone className="h-4 w-4" />
              Chiama {config.phone}
            </a>
          </Reveal>
          <div className="grid content-center gap-4 sm:grid-cols-2">
            {[
              { icon: Truck, title: "24/48h", text: "Spedizione espressa con corriere in tutta Italia." },
              { icon: ShieldCheck, title: "Qualità garantita", text: "Ricambi originali e compatibili testati." },
              { icon: CreditCard, title: "Pagamento con carta", text: "Paghi online in sicurezza tramite POS SumUp del negozio." },
              ...(config.bonifico_enabled
                ? [{ icon: Landmark, title: "Bonifico bancario", text: "Paghi con bonifico, spediamo all'accredito." }]
                : [{ icon: Phone, title: "Ordine telefonico", text: `Chiami il ${config.phone} e ordiniamo insieme.` }]),
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="h-full rounded-xl border border-white/10 bg-[#14171D]/80 p-5 backdrop-blur">
                  <f.icon className="h-6 w-6 text-[#FF5500]" />
                  <h3 className="mt-3 font-display text-lg font-semibold uppercase tracking-wide text-neutral-100">
                    {f.title}
                  </h3>
                  <p className="mt-1 text-sm text-neutral-400">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PAGAMENTI */}
      <section id="pagamenti" className="mx-auto max-w-7xl px-4 py-24" data-testid="payments-section">
        <Reveal>
          <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">Come paghi</p>
          <h2 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-neutral-100 sm:text-5xl">
            Pagamento semplice e sicuro
          </h2>
        </Reveal>
        <div className={`mt-12 grid gap-6 ${config.bonifico_enabled ? "md:grid-cols-2" : "max-w-2xl"}`}>
          <Reveal>
            <div className="h-full rounded-2xl border border-[#FF5500]/40 bg-gradient-to-br from-[#1C2028] to-[#14171D] p-8" data-testid="payment-info-card">
              <span className="rounded-full bg-[#FF5500] px-3 py-1 font-display text-[11px] uppercase tracking-[0.2em] text-white">
                Istantaneo
              </span>
              <CreditCard className="mt-6 h-10 w-10 text-[#FF5500]" />
              <h3 className="mt-4 font-display text-2xl font-semibold uppercase tracking-wide text-neutral-100">
                Carta di credito / debito
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                Al checkout scegli "Carta": ti apriamo il link di pagamento POS SumUp del negozio e paghi in
                sicurezza con Visa, Mastercard o Bancomat. L'ordine parte subito.
              </p>
            </div>
          </Reveal>
          {config.bonifico_enabled && (
            <Reveal delay={0.1}>
              <div className="h-full rounded-2xl border border-white/10 bg-[#14171D] p-8" data-testid="payment-info-bonifico">
                <span className="rounded-full border border-white/20 px-3 py-1 font-display text-[11px] uppercase tracking-[0.2em] text-neutral-300">
                  Zero commissioni
                </span>
                <Landmark className="mt-6 h-10 w-10 text-[#FF5500]" />
                <h3 className="mt-4 font-display text-2xl font-semibold uppercase tracking-wide text-neutral-100">
                  Bonifico bancario
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-300">
                  Scegli "Bonifico" al checkout: ti mostriamo le coordinate e la causale con il numero
                  d'ordine. Spediamo appena riceviamo l'accredito.
                </p>
                {config.iban?.iban && (
                  <button
                    onClick={copyIban}
                    data-testid="copy-iban-btn"
                    className="mt-5 flex w-full items-center justify-between gap-3 rounded-lg border border-white/10 bg-[#0C0E12] px-4 py-3 text-left transition-colors hover:border-[#FF5500]/50"
                  >
                    <span>
                      <span className="block text-[11px] uppercase tracking-widest text-neutral-500">IBAN negozio</span>
                      <span className="font-mono text-sm text-neutral-200">{config.iban.iban}</span>
                    </span>
                    {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4 text-neutral-400" />}
                  </button>
                )}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* CONTATTI / CTA FINALE */}
      <section id="contatti" className="border-t border-white/10 bg-[#08090C] py-24" data-testid="contact-section">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <Reveal>
            <p className="font-display text-xs uppercase tracking-[0.4em] text-[#FF5500]">
              Assistenza diretta · Lun–Sab 9:00–19:00
            </p>
            <h2 className="mt-4 font-display text-4xl font-bold uppercase tracking-tight text-neutral-100 sm:text-5xl">
              Parla con il negozio
            </h2>
            <a
              href={config.phone_href}
              data-testid="official-phone-display"
              className="mt-8 block font-display text-4xl font-bold tracking-wide text-[#FF5500] transition-transform hover:scale-[1.02] sm:text-6xl"
            >
              {config.phone}
            </a>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-neutral-400">
              Disponibilità in tempo reale, verifica compatibilità e aiuto con l'ordine: una telefonata
              e risolviamo tutto.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <a
                href={config.phone_href}
                data-testid="contact-call-btn"
                className="flex items-center gap-3 rounded-full bg-[#FF5500] px-8 py-4 font-display text-base uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#E64D00]"
              >
                <Phone className="h-4 w-4" /> Chiama ora
              </a>
              <Link
                to="/catalogo"
                data-testid="contact-catalog-btn"
                className="flex items-center gap-3 rounded-full border border-white/25 px-8 py-4 font-display text-base uppercase tracking-[0.15em] text-neutral-100 transition-colors hover:border-[#FF5500] hover:text-[#FF5500]"
              >
                Vai al catalogo <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
